import React, { useEffect, useState, useCallback, useMemo } from "react";
import { config } from "~/core";
import { apiProvider, getRoles } from "../api/core/provider";
import { apiAvailability } from "../api/availability";
import { apiWorkAreas } from "../api/workareas";
import { toast, ToastContainer } from "react-toastify";
import FSButton from "../components/Buttons/FSButton";
import { useTour } from "../tours/useTour";

const DAYS = [
  { key: "monday", label: "Mon", weekday: 1 },
  { key: "tuesday", label: "Tue", weekday: 2 },
  { key: "wednesday", label: "Wed", weekday: 3 },
  { key: "thursday", label: "Thu", weekday: 4 },
  { key: "friday", label: "Fri", weekday: 5 },
  { key: "saturday", label: "Sat", weekday: 6 },
];

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Monday of the week containing `date` (JS getDay: 0=Sun..6=Sat)
function mondayOf(date) {
  const jsd = date.getDay();
  const m = new Date(date);
  m.setHours(0, 0, 0, 0);
  m.setDate(m.getDate() - (jsd === 0 ? 6 : jsd - 1));
  return m;
}

function parseDate(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  let startPad = firstDay.getDay() - 1;
  if (startPad < 0) {startPad = 6;}
  for (let i = startPad - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }

  while (days.length % 7 !== 0) {
    const last = days[days.length - 1].date;
    const next = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
    days.push({ date: next, isCurrentMonth: false });
  }

  return days;
}

// JS getDay(): 0=Sun,1=Mon..6=Sat. Our weekday: 1=Mon..6=Sat
function jsWeekdayToOurs(jsDay) {
  return jsDay === 0 ? 7 : jsDay; // Sunday=7 (unused), Mon=1..Sat=6
}

export default function InspectorAvailability() {
  const isManager = getRoles().includes("manager");
  const currentUserId = config.user.id;

  const [inspectors, setInspectors] = useState(null);
  const [selectedInspectorId, setSelectedInspectorId] = useState(
    isManager ? null : currentUserId
  );
  const [schedule, setSchedule] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [dayLocations, setDayLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Work areas (read-only list here; managed on /work-areas)
  const [workAreas, setWorkAreas] = useState([]);

  // Calendar state
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());

  // Override modal state
  const [overrideModal, setOverrideModal] = useState(null);

  // Roster (team view): week-based matrix of inspector × day → area
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [rosterMap, setRosterMap] = useState({});
  const [rosterLoading, setRosterLoading] = useState(false);

  // Load roster when no inspector selected (manager view)
  useEffect(() => {
    if (!isManager || selectedInspectorId) {
      setRosterMap({});
      return;
    }
    setRosterLoading(true);
    const saturday = addDays(weekStart, 5);
    const months = [{ y: weekStart.getFullYear(), m: weekStart.getMonth() + 1 }];
    const sm = { y: saturday.getFullYear(), m: saturday.getMonth() + 1 };
    if (sm.y !== months[0].y || sm.m !== months[0].m) {months.push(sm);}
    Promise.all(months.map((mm) => apiAvailability.getOverview(mm.y, mm.m)))
      .then((responses) => {
        const map = {};
        responses.forEach((res) => {
          (res.data.result || []).forEach((day) => {
            map[day.date] = {};
            (day.inspectors || []).forEach((e) => { map[day.date][e.userId] = e.areaName || ""; });
          });
        });
        setRosterMap(map);
      })
      .catch(() => { toast.error("Error loading roster"); })
      .finally(() => { setRosterLoading(false); });
  }, [isManager, selectedInspectorId, weekStart]);

  // Load inspectors for admin view
  useEffect(() => {
    if (!isManager) {return;}
    apiProvider.get("inspectors").then((response) => {
      setInspectors((response.data.result || []).filter((i) => !i.isFolder));
    }).catch(() => {
      toast.error("Error loading inspectors");
    });
  }, [isManager]);

  // Load work areas
  const loadWorkAreas = useCallback(() => {
    apiWorkAreas.list().then((response) => {
      setWorkAreas(response.data.result || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadWorkAreas();
  }, [loadWorkAreas]);

  // Load availability when inspector is selected
  const loadAvailability = useCallback(() => {
    if (!selectedInspectorId) {return;}
    setLoading(true);
    apiAvailability.getInspectorAvailability(selectedInspectorId).then((response) => {
      const data = response.data.result || {};
      setSchedule(data.schedule || null);
      setOverrides(data.overrides || []);
      setDayLocations(data.dayLocations || []);
    }).catch(() => {
      toast.error("Error loading availability");
    }).finally(() => {
      setLoading(false);
    });
  }, [selectedInspectorId]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  // Save schedule
  const saveSchedule = async (updatedSchedule) => {
    setSavingSchedule(true);
    try {
      await apiAvailability.updateSchedule(selectedInspectorId, updatedSchedule);
      setSchedule({ ...schedule, ...updatedSchedule });
      toast.success("Schedule updated");
    } catch {
      toast.error("Error saving schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleDay = (dayKey) => {
    const current = schedule ? schedule[dayKey] : false;
    const updated = {
      monday: schedule?.monday || false,
      tuesday: schedule?.tuesday || false,
      wednesday: schedule?.wednesday || false,
      thursday: schedule?.thursday || false,
      friday: schedule?.friday || false,
      saturday: schedule?.saturday || false,
      defaultWorkAreaId: schedule?.defaultWorkAreaId || 0,
      [dayKey]: !current,
    };
    saveSchedule(updated);
  };

  const setDefaultArea = (areaId) => {
    const updated = {
      monday: schedule?.monday || false,
      tuesday: schedule?.tuesday || false,
      wednesday: schedule?.wednesday || false,
      thursday: schedule?.thursday || false,
      friday: schedule?.friday || false,
      saturday: schedule?.saturday || false,
      defaultWorkAreaId: areaId,
    };
    saveSchedule(updated);
  };


  const setDayArea = async (weekday, workAreaId) => {
    try {
      await apiAvailability.setDayLocation(selectedInspectorId, weekday, workAreaId);
      loadAvailability();
      toast.success("Day location updated");
    } catch {
      toast.error("Error updating day location");
    }
  };

  // Override management
  const handleDayClick = (date) => {
    if (date.getDay() === 0) {return;}
    const dateStr = formatDate(date);
    const existing = overrides.find((o) => o.date && o.date.startsWith(dateStr));
    if (existing) {
      apiAvailability.removeOverride(selectedInspectorId, existing.id).then(() => {
        setOverrides(overrides.filter((o) => o.id !== existing.id));
        toast.success("Override removed");
      }).catch(() => {
        toast.error("Error removing override");
      });
    } else {
      setOverrideModal({ date: dateStr, isAvailable: true, reason: "", workAreaId: 0 });
    }
  };

  const saveOverride = async () => {
    if (!overrideModal) {return;}
    try {
      await apiAvailability.addOverride(selectedInspectorId, {
        date: overrideModal.date,
        isAvailable: overrideModal.isAvailable,
        reason: overrideModal.reason,
        workAreaId: overrideModal.workAreaId,
      });
      setOverrideModal(null);
      loadAvailability();
      toast.success("Override added");
    } catch {
      toast.error("Error adding override");
    }
  };

  // Work area management lives on /work-areas now.

  // Helper to get area name by id
  const getAreaName = (areaId) => {
    if (!areaId) {return null;}
    const area = workAreas.find((a) => a.id === areaId);
    return area ? area.name : null;
  };

  // Get the effective area for a calendar day
  const getEffectiveArea = (date) => {
    const dateStr = formatDate(date);
    const override = overrides.find((o) => o.date && o.date.startsWith(dateStr));
    if (override && override.workAreaId > 0) {return getAreaName(override.workAreaId);}

    const weekday = jsWeekdayToOurs(date.getDay());
    const dayLoc = dayLocations.find((dl) => dl.weekday === weekday);
    if (dayLoc) {return dayLoc.workArea?.name || getAreaName(dayLoc.workAreaId);}

    if (schedule?.defaultWorkAreaId > 0) {return getAreaName(schedule.defaultWorkAreaId);}
    return null;
  };

  // Day location for a specific weekday
  const getDayLocationAreaId = (weekday) => {
    const dl = dayLocations.find((dl) => dl.weekday === weekday);
    return dl ? dl.workAreaId : 0;
  };

  // Determine day status for calendar display
  const getDayStatus = (date) => {
    if (date.getDay() === 0) {return "sunday";}

    const dateStr = formatDate(date);
    const override = overrides.find((o) => o.date && o.date.startsWith(dateStr));
    if (override) {
      return override.isAvailable ? "override-available" : "override-unavailable";
    }

    if (!schedule) {return "no-schedule";}

    const dayKey = DAYS.find(
      (d) => d.key === ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][date.getDay()]
    );
    if (dayKey && schedule[dayKey.key]) {return "scheduled";}
    return "off";
  };

  const dayStatusClasses = {
    "sunday": "tw-bg-gray-100 tw-text-gray-400",
    "no-schedule": "tw-bg-gray-50 tw-text-gray-400",
    "scheduled": "tw-bg-green-100 tw-text-green-800 hover:tw-bg-green-200 tw-cursor-pointer",
    "off": "tw-bg-gray-50 tw-text-gray-500 hover:tw-bg-gray-100 tw-cursor-pointer",
    "override-available": "tw-bg-blue-100 tw-text-blue-800 tw-ring-2 tw-ring-blue-400 hover:tw-bg-blue-200 tw-cursor-pointer",
    "override-unavailable": "tw-bg-red-100 tw-text-red-800 tw-ring-2 tw-ring-red-400 hover:tw-bg-red-200 tw-cursor-pointer",
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else {setCalMonth(calMonth - 1);}
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else {setCalMonth(calMonth + 1);}
  };

  const calendarDays = getCalendarDays(calYear, calMonth);
  const daysPerWeek = schedule ? DAYS.filter((d) => schedule[d.key]).length : 0;

  const rosterDays = DAYS.map((d) => addDays(weekStart, d.weekday - 1));
  const todayStr = formatDate(new Date());

  const tourSteps = useMemo(() => [
    {
      element: '[data-tour="ia-title"]',
      popover: {
        title: "Inspector Availability",
        description: "Set which work area each inspector is assigned to, per weekday. This is what the auto-scheduler plans against.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="ia-select"]',
      popover: {
        title: "Pick an inspector",
        description: "Select an inspector to edit their weekly schedule and area assignments, or browse the Team Roster below.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="ia-roster"]',
      popover: {
        title: "Team Roster",
        description: "A week-at-a-glance of which work area each inspector is assigned to each day.",
        side: "right", align: "start",
      },
    },
    {
      element: '[data-tour="ia-week"]',
      popover: {
        title: "Move week to week",
        description: "Step backward and forward a week, or jump back to This week.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour="ia-table"]',
      popover: {
        title: "Inspect the roster",
        description: "Each cell shows the inspector's assigned area for that day, or OFF. Click an inspector's name to edit their schedule.",
        side: "top", align: "start",
      },
    },
  ], []);

  const tourEnabled = isManager && !selectedInspectorId && !!inspectors && !rosterLoading;
  const { startTour, showButton } = useTour({ tourKey: "inspector-availability", steps: tourSteps, enabled: tourEnabled });

  return (
    <div className="tw-max-w-7xl tw-mx-auto tw-p-6">
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
      />
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
        <h1 data-tour="ia-title" className="tw-text-2xl tw-font-bold">Inspector Availability</h1>
        {showButton && <button onClick={startTour} className="tw-px-3 tw-py-1.5 tw-border tw-rounded tw-text-sm hover:tw-bg-gray-50">Take the tour</button>}
      </div>

      {/* Inspector selector - admin only */}
      {isManager &&
        <div data-tour="ia-select" className="tw-mb-6">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Inspector
          </label>
          <select
            className="tw-w-full tw-max-w-md tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2"
            value={selectedInspectorId || ""}
            onChange={(e) => setSelectedInspectorId(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Select an inspector...</option>
            {inspectors && inspectors.map((insp) =>
              <option key={insp.id} value={insp.id}>{insp.name}</option>
            )}
          </select>
        </div>
      }

      {/* Roster matrix when no inspector selected */}
      {isManager && !selectedInspectorId &&
        <div data-tour="ia-roster" className="tw-mb-8">
          <h2 className="tw-text-lg tw-font-semibold tw-mb-3">Team Roster</h2>

          {/* Week navigation */}
          <div data-tour="ia-week" className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
            <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="tw-px-3 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50">&lt;</button>
            <span className="tw-font-medium tw-text-lg tw-w-64 tw-text-center">
              {MONTH_NAMES[rosterDays[0].getMonth()].slice(0, 3)} {rosterDays[0].getDate()}{rosterDays[0].getFullYear() !== rosterDays[5].getFullYear() ? " " + rosterDays[0].getFullYear() : ""}–{MONTH_NAMES[rosterDays[5].getMonth()].slice(0, 3)} {rosterDays[5].getDate()}, {rosterDays[5].getFullYear()}
            </span>
            <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="tw-px-3 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50">&gt;</button>
            <button onClick={() => setWeekStart(mondayOf(new Date()))} className="tw-ml-2 tw-px-3 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50 tw-text-sm">This week</button>
          </div>

          {rosterLoading && <p className="tw-text-gray-500">Loading...</p>}

          {!rosterLoading && inspectors &&
            <div data-tour="ia-table" className="tw-overflow-x-auto">
              <table className="tw-min-w-full tw-border tw-rounded tw-text-sm">
                <thead>
                  <tr className="tw-bg-gray-50">
                    <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600 tw-w-44">Inspector</th>
                    {rosterDays.map((d, i) => {
                      const ds = formatDate(d);
                      const isToday = ds === todayStr;
                      return (
                        <th key={i} className={`tw-px-2 tw-py-2 tw-text-center tw-font-medium tw-text-xs ${isToday ? "tw-text-blue-700" : "tw-text-gray-500"}`}>
                          <div>{["Mon","Tue","Wed","Thu","Fri","Sat"][i]}</div>
                          <div className="tw-text-gray-400">{d.getDate()}{d.getMonth() === rosterDays[0].getMonth() ? "" : " " + MONTH_NAMES[d.getMonth()].slice(0, 3)}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {[...inspectors].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((insp) => {
                    return (
                      <tr key={insp.id} className="tw-border-t hover:tw-bg-gray-50">
                        <td className="tw-px-3 tw-py-2">
                          <button onClick={() => setSelectedInspectorId(insp.id)} className="tw-text-left hover:tw-text-blue-600 hover:tw-underline" title="Edit this inspector's schedule">
                            {insp.name}
                          </button>
                        </td>
                        {rosterDays.map((d, i) => {
                          const ds = formatDate(d);
                          const area = (rosterMap[ds] || {})[insp.id];
                          const isToday = ds === todayStr;
                          return (
                            <td key={i} className={`tw-px-2 tw-py-2 tw-text-center tw-text-xs ${isToday ? "tw-bg-blue-50" : ""}`}>
                              {area
                                ? <span className="tw-inline-block tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-green-100 tw-text-green-800" title={area}>{area}</span>
                                : <span className="tw-text-gray-300">OFF</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          }
        </div>
      }

      {!isManager && !selectedInspectorId &&
        <p className="tw-text-gray-500">Loading your availability...</p>
      }

      {selectedInspectorId && loading &&
        <p className="tw-text-gray-500">Loading...</p>
      }

      {selectedInspectorId && !loading &&
        <>
          {/* Weekly Schedule */}
          <div className="tw-mb-8">
            <h2 className="tw-text-lg tw-font-semibold tw-mb-3">Weekly Schedule</h2>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
              {daysPerWeek > 0
                ? `${daysPerWeek} day${daysPerWeek !== 1 ? "s" : ""} per week`
                : "No days scheduled - this inspector is not available for auto-planning"}
            </p>

            {/* Day toggles */}
            <div className="tw-flex tw-gap-2 tw-mb-4">
              {DAYS.map((day) => {
                const isOn = schedule ? schedule[day.key] : false;
                return (
                  <button
                    key={day.key}
                    onClick={() => toggleDay(day.key)}
                    disabled={savingSchedule}
                    className={`tw-px-4 tw-py-2 tw-rounded tw-font-medium tw-text-sm tw-transition-colors tw-border ${
                      isOn
                        ? "tw-bg-green-500 tw-text-white tw-border-green-600 hover:tw-bg-green-600"
                        : "tw-bg-gray-100 tw-text-gray-600 tw-border-gray-300 hover:tw-bg-gray-200"
                    } ${savingSchedule ? "tw-opacity-50" : ""}`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>

            {/* Default work area */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-3">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 tw-w-32">Default area:</label>
              <select
                className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-48"
                value={schedule?.defaultWorkAreaId || 0}
                onChange={(e) => setDefaultArea(parseInt(e.target.value))}
              >
                <option value={0}>None</option>
                {workAreas.map((area) =>
                  <option key={area.id} value={area.id}>{area.name}</option>
                )}
              </select>
            </div>


            {/* Per-day area overrides */}
            {daysPerWeek > 0 && workAreas.length > 0 &&
              <div className="tw-space-y-1">
                {DAYS.filter((day) => schedule && schedule[day.key]).map((day) => {
                  const currentAreaId = getDayLocationAreaId(day.weekday);
                  return (
                    <div key={day.key} className="tw-flex tw-items-center tw-gap-3">
                      <label className="tw-text-sm tw-text-gray-600 tw-w-32">{day.label}:</label>
                      <select
                        className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-w-48"
                        value={currentAreaId}
                        onChange={(e) => setDayArea(day.weekday, parseInt(e.target.value))}
                      >
                        <option value={0}>Use default</option>
                        {workAreas.map((area) =>
                          <option key={area.id} value={area.id}>{area.name}</option>
                        )}
                      </select>
                    </div>
                  );
                })}
              </div>
            }
          </div>


          {/* Calendar / Overrides */}
          <div className="tw-mb-8">
            <h2 className="tw-text-lg tw-font-semibold tw-mb-3">Schedule Overrides</h2>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
              Click a day to add or remove an override. Overrides take priority over the weekly schedule.
            </p>

            {/* Calendar navigation */}
            <div data-tour="ia-week" className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
              <button onClick={prevMonth} className="tw-px-3 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50">&lt;</button>
              <span className="tw-font-medium tw-text-lg tw-w-48 tw-text-center">
                {MONTH_NAMES[calMonth]} {calYear}
              </span>
              <button onClick={nextMonth} className="tw-px-3 tw-py-1 tw-border tw-rounded hover:tw-bg-gray-50">&gt;</button>
            </div>

            {/* Calendar grid */}
            <div className="tw-border tw-rounded tw-overflow-hidden">
              <div className="tw-grid tw-grid-cols-7 tw-bg-gray-50">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) =>
                  <div key={d} className="tw-text-center tw-text-xs tw-font-medium tw-text-gray-500 tw-py-2">
                    {d}
                  </div>
                )}
              </div>
              <div className="tw-grid tw-grid-cols-7">
                {calendarDays.map((day, i) => {
                  const status = getDayStatus(day.date);
                  const isSunday = day.date.getDay() === 0;
                  const dateStr = formatDate(day.date);
                  const override = overrides.find((o) => o.date && o.date.startsWith(dateStr));
                  const today = formatDate(new Date());
                  const areaName = !isSunday && status !== "off" && status !== "no-schedule"
                    ? getEffectiveArea(day.date)
                    : null;

                  return (
                    <div
                      key={i}
                      onClick={() => !isSunday && day.isCurrentMonth && handleDayClick(day.date)}
                      className={`tw-p-1 tw-text-center tw-text-sm tw-border-t tw-min-h-[3.5rem] tw-flex tw-flex-col tw-items-center tw-justify-center ${
                        dayStatusClasses[status] || ""
                      } ${!day.isCurrentMonth ? "tw-opacity-40" : ""} ${
                        dateStr === today ? "tw-font-bold" : ""
                      }`}
                    >
                      <span>{day.date.getDate()}</span>
                      {override &&
                        <span className="tw-text-[10px] tw-leading-tight">
                          {override.isAvailable ? "IN" : "OFF"}
                        </span>
                      }
                      {areaName &&
                        <span className="tw-text-[9px] tw-leading-tight tw-text-gray-600 tw-truncate tw-max-w-full tw-px-0.5">
                          {areaName}
                        </span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="tw-flex tw-gap-4 tw-mt-3 tw-text-xs tw-text-gray-600">
              <span className="tw-flex tw-items-center tw-gap-1">
                <span className="tw-w-3 tw-h-3 tw-rounded tw-bg-green-100 tw-border tw-border-green-300"></span> Scheduled
              </span>
              <span className="tw-flex tw-items-center tw-gap-1">
                <span className="tw-w-3 tw-h-3 tw-rounded tw-bg-blue-100 tw-border tw-border-blue-400"></span> Override: Available
              </span>
              <span className="tw-flex tw-items-center tw-gap-1">
                <span className="tw-w-3 tw-h-3 tw-rounded tw-bg-red-100 tw-border tw-border-red-400"></span> Override: Unavailable
              </span>
              <span className="tw-flex tw-items-center tw-gap-1">
                <span className="tw-w-3 tw-h-3 tw-rounded tw-bg-gray-50 tw-border tw-border-gray-300"></span> Off
              </span>
            </div>
          </div>

          {/* Upcoming overrides list */}
          {overrides.length > 0 &&
            <div>
              <h2 className="tw-text-lg tw-font-semibold tw-mb-3">Upcoming Overrides</h2>
              <div className="tw-border tw-rounded tw-divide-y">
                {overrides.map((override) => {
                  const date = override.date ? parseDate(override.date.substring(0, 10)) : null;
                  const areaName = override.workAreaId ? getAreaName(override.workAreaId) : null;
                  return (
                    <div key={override.id} className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                      <div>
                        <span className="tw-font-medium">
                          {date ? date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : ""}
                        </span>
                        <span className={`tw-ml-3 tw-text-sm tw-px-2 tw-py-0.5 tw-rounded ${
                          override.isAvailable
                            ? "tw-bg-blue-100 tw-text-blue-700"
                            : "tw-bg-red-100 tw-text-red-700"
                        }`}>
                          {override.isAvailable ? "Available" : "Unavailable"}
                        </span>
                        {areaName &&
                          <span className="tw-ml-2 tw-text-sm tw-text-gray-600">@ {areaName}</span>
                        }
                        {override.reason &&
                          <span className="tw-ml-3 tw-text-sm tw-text-gray-500">{override.reason}</span>
                        }
                      </div>
                      <button
                        onClick={() => {
                          apiAvailability.removeOverride(selectedInspectorId, override.id).then(() => {
                            setOverrides(overrides.filter((o) => o.id !== override.id));
                            toast.success("Override removed");
                          }).catch(() => {
                            toast.error("Error removing override");
                          });
                        }}
                        className="tw-text-red-500 hover:tw-text-red-700 tw-text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          }
        </>
      }

      {/* Override Modal */}
      {overrideModal &&
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-w-96 tw-shadow-xl">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-4">
              Add Override for {parseDate(overrideModal.date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </h3>
            <div className="tw-mb-4">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Status</label>
              <div className="tw-flex tw-gap-2">
                <button
                  onClick={() => setOverrideModal({ ...overrideModal, isAvailable: true })}
                  className={`tw-flex-1 tw-py-2 tw-rounded tw-border tw-text-sm tw-font-medium ${
                    overrideModal.isAvailable
                      ? "tw-bg-blue-500 tw-text-white tw-border-blue-600"
                      : "tw-bg-white tw-text-gray-600 tw-border-gray-300"
                  }`}
                >
                  Available
                </button>
                <button
                  onClick={() => setOverrideModal({ ...overrideModal, isAvailable: false })}
                  className={`tw-flex-1 tw-py-2 tw-rounded tw-border tw-text-sm tw-font-medium ${
                    !overrideModal.isAvailable
                      ? "tw-bg-red-500 tw-text-white tw-border-red-600"
                      : "tw-bg-white tw-text-gray-600 tw-border-gray-300"
                  }`}
                >
                  Unavailable
                </button>
              </div>
            </div>
            {overrideModal.isAvailable && workAreas.length > 0 &&
              <div className="tw-mb-4">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                  Work Area (optional)
                </label>
                <select
                  className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm"
                  value={overrideModal.workAreaId}
                  onChange={(e) => setOverrideModal({ ...overrideModal, workAreaId: parseInt(e.target.value) })}
                >
                  <option value={0}>Use scheduled area</option>
                  {workAreas.map((area) =>
                    <option key={area.id} value={area.id}>{area.name}</option>
                  )}
                </select>
              </div>
            }
            <div className="tw-mb-4">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={overrideModal.reason}
                onChange={(e) => setOverrideModal({ ...overrideModal, reason: e.target.value })}
                placeholder="e.g., Vacation, Working Saturday"
                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm"
              />
            </div>
            <div className="tw-flex tw-gap-2 tw-justify-end">
              <button
                onClick={() => setOverrideModal(null)}
                className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 hover:tw-text-gray-800"
              >
                Cancel
              </button>
              <FSButton onClick={saveOverride}>Save Override</FSButton>
            </div>
          </div>
        </div>
      }
    </div>
  );
}
